import { useEffect, useRef, useState } from 'react';
import { ADMIN_API } from '@formosa/shared/constants/endpoints';
import { ADMIN_STORAGE_KEYS } from '@formosa/shared/constants/storageKeys';

const QUEUE_TYPES = new Set([
  'SUPPORT_QUEUE_ITEM_ADDED',
  'SUPPORT_QUEUE_ITEM_UPDATED',
  'SUPPORT_QUEUE_ITEM_REMOVED',
  'SUPPORT_QUEUE_REORDERED',
]);

const PRESENCE_TYPES = new Set([
  'AGENT_ONLINE',
  'AGENT_OFFLINE',
  'AGENT_STATUS_CHANGED',
]);

/**
 * Admin CS SSE. Falls back silently — callers should keep a slow poll as backup.
 */
export default function useCsAdminSse({
  enabled = true,
  onQueueEvent,
  onPresenceEvent,
  onSessionStatus,
  onMessageCreated,
} = {}) {
  const [connected, setConnected] = useState(false);
  const [lastError, setLastError] = useState('');
  const handlersRef = useRef({ onQueueEvent, onPresenceEvent, onSessionStatus, onMessageCreated });
  handlersRef.current = { onQueueEvent, onPresenceEvent, onSessionStatus, onMessageCreated };

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || typeof EventSource === 'undefined') {
      return undefined;
    }
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    if (!token) {
      setLastError('missing token');
      return undefined;
    }
    const url = `${ADMIN_API}/im/admin/customer-service/events?access_token=${encodeURIComponent(token)}`;
    let es;
    let closed = false;
    try {
      es = new EventSource(url);
    } catch (err) {
      setLastError(err?.message || 'EventSource failed');
      return undefined;
    }

    const handleEnvelope = (raw) => {
      let event;
      try {
        event = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch {
        return;
      }
      if (!event?.eventType) return;
      const type = event.eventType;
      const h = handlersRef.current;
      if (QUEUE_TYPES.has(type)) h.onQueueEvent?.(event);
      else if (PRESENCE_TYPES.has(type)) h.onPresenceEvent?.(event);
      else if (type === 'SUPPORT_SESSION_STATUS_CHANGED') h.onSessionStatus?.(event);
      else if (type === 'SUPPORT_MESSAGE_CREATED') h.onMessageCreated?.(event);
    };

    es.onopen = () => {
      if (!closed) {
        setConnected(true);
        setLastError('');
      }
    };
    es.onerror = () => {
      if (!closed) {
        setConnected(false);
        setLastError('sse disconnected');
      }
    };

    const types = [
      'connected',
      ...QUEUE_TYPES,
      ...PRESENCE_TYPES,
      'SUPPORT_SESSION_STATUS_CHANGED',
      'SUPPORT_MESSAGE_CREATED',
    ];
    types.forEach((name) => {
      es.addEventListener(name, (ev) => {
        if (name === 'connected') {
          setConnected(true);
          return;
        }
        handleEnvelope(ev.data);
      });
    });
    // Also handle unnamed messages if any
    es.onmessage = (ev) => handleEnvelope(ev.data);

    return () => {
      closed = true;
      setConnected(false);
      try {
        es.close();
      } catch {
        /* ignore */
      }
    };
  }, [enabled]);

  return { connected, lastError };
}
